import React from 'react';
import NotificationDeepLinkPicker from './NotificationDeepLinkPicker';
import WhatsAppSearchSelect from './WhatsAppSearchSelect';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';
import { mergeNotificationFields, notificationFieldSelectOptions } from './whatsappNotificationFields';
import type { WhatsAppSendRecipient, WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import {
  getSendRecipientFields,
  resolveSendRecipientFieldValue,
  type SendTemplateVariableSlot,
} from './whatsappSendRecipientFields';

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';

type Props = {
  slots: SendTemplateVariableSlot[];
  variableLabels: string[];
  recipients: WhatsAppSendRecipient[];
  contextFields: WhatsAppContextField[];
  campaigns: DeepLinkCampaign[];
  onCampaignCreated: (campaign: DeepLinkCampaign) => void;
  onChange: (next: SendTemplateVariableSlot[]) => void;
  disabled?: boolean;
};

export default function WhatsAppSendVariableEditor({
  slots,
  variableLabels,
  recipients,
  contextFields,
  campaigns,
  onCampaignCreated,
  onChange,
  disabled,
}: Props) {
  const allFields = React.useMemo(
    () => mergeNotificationFields(contextFields),
    [contextFields]
  );
  const fieldOptions = React.useMemo(() => {
    const selectedKeys = slots.map((s) => s.fieldKey);
    const options = notificationFieldSelectOptions(allFields, selectedKeys);
    const extra = getSendRecipientFields(recipients).filter((f) => {
      const key = String(f.key || '').toLowerCase();
      if (options.some((o) => o.value.toLowerCase() === key)) return false;
      if (key === 'name' && options.some((o) => o.value.toLowerCase() === 'patientname')) return false;
      return true;
    });
    return [
      ...options,
      ...extra.map((f) => ({
        value: f.key,
        label: f.label,
        hint: f.key,
        group: 'Recipient',
      })),
    ];
  }, [allFields, recipients, slots]);
  const previewRecipient = recipients[0];
  const campaignsById = React.useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);

  const updateSlot = (slot: number, patch: Partial<SendTemplateVariableSlot>) => {
    onChange(slots.map((s) => (s.slot === slot ? { ...s, ...patch } : s)));
  };

  if (!slots.length) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Map each {'{{n}}'} to a notification field, a deep link URL, or the same static text for everyone.
        {recipients.length > 1 ? ' Notification fields and dynamic deep links change per contact.' : null}
      </p>
      {slots.map((slot, index) => (
        <VariableSlotRow
          key={slot.slot}
          slot={slot}
          label={variableLabels[index] || `Variable ${slot.slot}`}
          fieldOptions={fieldOptions}
          contextFields={allFields}
          campaigns={campaigns}
          campaignsById={campaignsById}
          previewRecipient={previewRecipient}
          disabled={disabled}
          onCampaignCreated={onCampaignCreated}
          onChange={(patch) => updateSlot(slot.slot, patch)}
        />
      ))}
    </div>
  );
}

function VariableSlotRow({
  slot,
  label,
  fieldOptions,
  contextFields,
  campaigns,
  campaignsById,
  previewRecipient,
  disabled,
  onCampaignCreated,
  onChange,
}: {
  slot: SendTemplateVariableSlot;
  label: string;
  fieldOptions: Array<{ value: string; label: string; hint?: string; group?: string }>;
  contextFields: WhatsAppContextField[];
  campaigns: DeepLinkCampaign[];
  campaignsById: Map<string, DeepLinkCampaign>;
  previewRecipient?: WhatsAppSendRecipient;
  disabled?: boolean;
  onCampaignCreated: (campaign: DeepLinkCampaign) => void;
  onChange: (patch: Partial<SendTemplateVariableSlot>) => void;
}) {
  const previewValue =
    slot.sourceType === 'recipient_field' && slot.fieldKey && previewRecipient
      ? resolveSendRecipientFieldValue(slot.fieldKey, previewRecipient) || null
      : null;

  const deepLinkPreview =
    slot.sourceType === 'deep_link' && slot.deepLinkCampaignId
      ? previewDeepLinkFromCampaign(
          {
            deepLinkCampaignId: slot.deepLinkCampaignId,
            deepLinkUrlType: slot.deepLinkUrlType,
            deepLinkParamBindings: slot.deepLinkParamBindings,
          },
          campaignsById
        )
      : '';

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300">
        {`{{${slot.slot}}}`} — {label}
      </label>
      <div className="grid gap-2 sm:grid-cols-2">
        <select
          value={slot.sourceType}
          disabled={disabled}
          onChange={(e) => {
            const sourceType = e.target.value as SendTemplateVariableSlot['sourceType'];
            if (sourceType === 'recipient_field') {
              onChange({
                sourceType,
                fieldKey: slot.fieldKey || fieldOptions[0]?.value || 'name',
                staticValue: undefined,
                deepLinkCampaignId: null,
              });
              return;
            }
            if (sourceType === 'deep_link') {
              onChange({
                sourceType,
                fieldKey: null,
                staticValue: undefined,
                deepLinkCampaignId: campaigns[0]?.id || null,
                deepLinkUrlType: 'short',
                deepLinkParamBindings: {},
              });
              return;
            }
            onChange({
              sourceType,
              fieldKey: null,
              deepLinkCampaignId: null,
              staticValue: slot.staticValue || '',
            });
          }}
          className={inputClass}
        >
          <option value="recipient_field">Notification field</option>
          <option value="deep_link">Deep link URL</option>
          <option value="static">Same text for everyone</option>
        </select>

        {slot.sourceType === 'recipient_field' ? (
          <WhatsAppSearchSelect
            value={slot.fieldKey || ''}
            disabled={disabled}
            onChange={(fieldKey) => onChange({ fieldKey: fieldKey || null })}
            options={fieldOptions}
            placeholder="Select notification field…"
            emptyLabel="Select notification field…"
            searchPlaceholder="Search fields…"
          />
        ) : slot.sourceType === 'static' ? (
          <input
            value={slot.staticValue || ''}
            disabled={disabled}
            onChange={(e) => onChange({ staticValue: e.target.value })}
            placeholder={`Enter text for {{${slot.slot}}}`}
            className={inputClass}
          />
        ) : (
          <span className="self-center text-xs text-gray-500">Configure deep link below</span>
        )}
      </div>

      {slot.sourceType === 'recipient_field' && (
        <p className="mt-2 text-xs text-[#128C7E]">
          Filled automatically per recipient
          {previewValue ? (
            <>
              {' '}
              — preview: <strong>{previewValue}</strong>
            </>
          ) : slot.fieldKey ? (
            <>
              {' '}
              — uses <strong>{slot.fieldKey}</strong> when available
            </>
          ) : null}
        </p>
      )}

      {slot.sourceType === 'deep_link' && (
        <div className="mt-3 space-y-2">
          <NotificationDeepLinkPicker
            compact
            value={{
              deepLinkCampaignId: slot.deepLinkCampaignId,
              deepLinkUrlType: slot.deepLinkUrlType,
              deepLinkParamBindings: slot.deepLinkParamBindings,
            }}
            onChange={(next) =>
              onChange({
                deepLinkCampaignId: next.deepLinkCampaignId,
                deepLinkUrlType: next.deepLinkUrlType,
                deepLinkParamBindings: next.deepLinkParamBindings,
              })
            }
            contextFields={contextFields}
            campaigns={campaigns}
            onCampaignCreated={onCampaignCreated}
            suggestedCampaignName={`WhatsApp send · {{${slot.slot}}}`}
          />
          {deepLinkPreview ? (
            <p className="truncate text-xs text-gray-500">
              Preview URL: <span className="font-mono text-[#128C7E]">{deepLinkPreview}</span>
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
